import {
  ColorPaletteIcon,
  defaultColorPalettes,
} from "@/Apps/Pages/Properties/components/sections/Appearance/Appearance";
import Button from "@/ui/Button";
import Calout from "@/ui/Calout";
import DetailsView from "@/ui/DataView/DetailsView";
import Panel from "@/ui/Panel";
import { ButtonTypes, Variants } from "@/ui/types";
import { AIMessage } from "@langchain/core/messages";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { useEffect, useState } from "react";

import { CampaignSpecSchema } from "@launchos/langgraph/Campaign/schema";
import ReactJson from "react-json-view";

export const CampaignSpecFromStructuredMessage = (props: any) => {
  const { payload, currentThread, setPinnedMessagesBottom } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [showFooter, setShowFooter] = useState(!payload?.campaignSpec?._fromInterrupt);

  const { campaignSpec = {} } = payload || {};

  const profileData = [
    {
      id: "company_name",
      label: "Company Name",
      value: campaignSpec?.profile?.company_name,
      fieldType: "string" as const,
    },
    {
      id: "business_model",
      label: "Business Model",
      value: campaignSpec?.profile?.business_model,
      fieldType: "string" as const,
    },
  ];

  const objectiveData = [
    {
      id: "goal",
      label: "Primary Goal",
      value: campaignSpec?.objective?.goal,
      fieldType: "string" as const,
    },
    {
      id: "awareness",
      label: "Target Awareness",
      value: campaignSpec?.objective?.awareness,
      fieldType: "string" as const,
    },
  ];

  const productData = [
    {
      id: "product_type",
      label: "Product Type",
      value: campaignSpec?.product_details?.product_type,
      fieldType: "string" as const,
    },
    {
      id: "price_point",
      label: "Price Point",
      value: campaignSpec?.product_details?.price_point,
      fieldType: "string" as const,
    },
    {
      id: "complexity",
      label: "Complexity",
      value: campaignSpec?.product_details?.complexity,
      fieldType: "string" as const,
    },
  ];

  const marketingData = [
    {
      id: "content_preference",
      label: "Content Preference",
      value: campaignSpec?.marketing_approach?.content_preference,
      fieldType: "string" as const,
    },
    {
      id: "selling_style",
      label: "Selling Style",
      value: campaignSpec?.marketing_approach?.selling_style,
      fieldType: "string" as const,
    },
    {
      id: "traffic_source",
      label: "Traffic Source",
      value: campaignSpec?.marketing_approach?.traffic_source,
      fieldType: "string" as const,
    },
    {
      id: "follow_up_ability",
      label: "Follow-up Ability",
      value: campaignSpec?.marketing_approach?.follow_up_ability,
      fieldType: "string" as const,
    },
  ];

  const designData = [
    {
      id: "color_palette",
      label: "Color Palette",
      value: campaignSpec?.design_guide?.colorPalette,
      fieldType: "custom" as const,
      component: ({
        value,
        isEditable,
        onItemChange,
      }: {
        value: string | number | boolean;
        isEditable: boolean;
        onItemChange: (data: any) => void;
      }) => {
        const palette = defaultColorPalettes.find((p) => p.id === String(value));
        
        if (!palette) {
          return <p className="text-sm flex items-center gap-2">
            {String(value).replace(/-/g, " ").replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase())}
          </p>
        }

        return (
          <p className="text-sm flex items-center gap-2">
            <ColorPaletteIcon palette={palette} className="rounded-full p-2" />
            {(palette?.label || "")
              .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase())}
          </p>
        );
      },
    },
    {
      id: "design",
      label: "Design Style",
      value: campaignSpec?.design_guide?.design,
      fieldType: "longtext" as const,
    },
    {
      id: "logo",
      label: "Logo URL",
      value: campaignSpec?.design_guide?.logo,
      fieldType: "string" as const,
    },
  ];

  const [newCampaignSpecData, setNewCampaignSpecData] = useState<any>({
    profile: profileData,
    objective: objectiveData,
    product_details: productData,
    marketing_approach: marketingData,
    design_guide: designData,
  });

  const [newCampaignSpec, setNewCampaignSpec] = useState<any>(campaignSpec);

  const handleProfileItemChange = async (id: string, allItems: any[]) => {
    const value = allItems.find((item: any) => item.id === id)?.value;
    setNewCampaignSpec({ ...newCampaignSpec, profile: { ...newCampaignSpec.profile, [id]: value }});
    setNewCampaignSpecData({ ...newCampaignSpecData, [id]: allItems });
  };

  const handleObjectiveItemChange = async (id: string, allItems: any[]) => {
    const value = allItems.find((item: any) => item.id === id)?.value;
    setNewCampaignSpec({ ...newCampaignSpec, objective: { ...newCampaignSpec.objective, [id]: value }});
    setNewCampaignSpecData({ ...newCampaignSpecData, [id]: allItems });
  };

  const handleProductDetailsItemChange = async (id: string, allItems: any[]) => {
    const value = allItems.find((item: any) => item.id === id)?.value;
    setNewCampaignSpec({ ...newCampaignSpec, product_details: { ...newCampaignSpec.product_details, [id]: value }});
    setNewCampaignSpecData({ ...newCampaignSpecData, [id]: allItems });
  };

  const handleMarketingApproachItemChange = async (id: string, allItems: any[]) => {
    const value = allItems.find((item: any) => item.id === id)?.value;
    setNewCampaignSpec({ ...newCampaignSpec, marketing_approach: { ...newCampaignSpec.marketing_approach, [id]: value }});
    setNewCampaignSpecData({ ...newCampaignSpecData, [id]: allItems });
  };

  const handleDesignGuideItemChange = async (id: string, allItems: any[]) => {
    const value = allItems.find((item: any) => item.id === id)?.value;
    setNewCampaignSpec({ ...newCampaignSpec, design_guide: { ...newCampaignSpec.design_guide, [id]: value }});
    setNewCampaignSpecData({ ...newCampaignSpecData, [id]: allItems });
  };

  const handleBuildCampaignAssets = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setShowFooter(false);

    setPinnedMessagesBottom([]);

    await currentThread.submit({
      campaignSpec: {
        ...newCampaignSpec,
        _fromInterrupt: true,
      },
    });

  };

  return (
    <div className="flex flex-col gap-2 max-w-2xl">
      {/* <ReactJson src={payload} collapsed /> */}
      <Panel
        title="Campaign Blueprint"
        className="w-full"
        actionButton={
          <Button
            type={ButtonTypes.SOFT}
            label={
              <>
                <PencilSimpleLine size={16} />
              </>
            }
            onClick={() => setIsEditing(!isEditing)}
          />
        }
      >
        <div className="flex flex-col gap-4">
          <Calout
            title="Campaign Specification"
            caption="This is the campaign specification that will be used to build your campaign."
          />

          <DetailsView
            title={<p className="mb-4">Company Profile</p>}
            item={{
              id: "profile",
              label: "Profile",
              data: newCampaignSpecData?.profile,
            }}
            visibleFields={["company_name", "business_model"]}
            isEditable={isEditing}
            onItemChange={handleProfileItemChange}
          />

          <DetailsView
            title={<p className="mb-4">Campaign Objective</p>}
            item={{
              id: "objective",
              label: "Objective",
              data: newCampaignSpecData?.objective,
            }}
            visibleFields={["goal", "awareness"]}
            isEditable={isEditing}
            onItemChange={handleObjectiveItemChange}
          />

          <div className="h-px bg-gray-200 dark:bg-white/10" />

          <DetailsView
            title={<p className="mb-4">Product Details</p>}
            item={{
              id: "product",
              label: "Product",
              data: newCampaignSpecData?.product_details,
            }}
            visibleFields={["product_type", "price_point", "complexity"]}
            isEditable={isEditing}
            onItemChange={handleProductDetailsItemChange}
          />

          <DetailsView
            title={<p className="mb-4">Marketing Approach</p>}
            item={{
              id: "marketing",
              label: "Marketing",
              data: newCampaignSpecData?.marketing_approach,
            }}
            visibleFields={[
              "content_preference",
              "selling_style",
              "traffic_source",
              "follow_up_ability",
            ]}
            isEditable={isEditing}
            onItemChange={handleMarketingApproachItemChange}
          />

          <div className="h-px bg-gray-200 dark:bg-white/10" />

          <DetailsView
            title={<p className="mb-4">Design Guide</p>}
            item={{
              id: "design",
              label: "Design",
              data: newCampaignSpecData?.design_guide,
            }}
            visibleFields={["colorPalette", "design", "logo"]}
            isEditable={isEditing}
            onItemChange={handleDesignGuideItemChange}
          >
            {showFooter && (
              <>
                <hr className="h-px bg-gray-200 dark:bg-gray-700 my-4" />
                <div
                  className={`flex mt-4 ${isEditing ? "justify-between" : "justify-end"}`}
                >
                  {isEditing && (
                    <Button
                      type={ButtonTypes.OUTLINED}
                      // variant={Variants.BLACK}
                      label="Cancel"
                      onClick={() => setIsEditing(false)}
                    />
                  )}
                  <Button
                    label="Build Campaign"
                    type={ButtonTypes.ROUNDED}
                    variant={Variants.BLACK}
                    onClick={handleBuildCampaignAssets}
                  />
                </div>
              </>
            )}
          </DetailsView>
        </div>
      </Panel>
    </div>
  );
};
